import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  Typography, 
  TextField, 
  IconButton,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Send as SendIcon, 
  School as SchoolIcon, 
  Person as PersonIcon,
  Class as MajorIcon
} from '@mui/icons-material';
import api from '../services/api';

const GuidanceChat = () => {
  const [tab, setTab] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [schools, setMySchools] = useState([]);
  const [majors, setMyMajors] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState<any>(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const chatEndRef = useRef<null | HTMLDivElement>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchLists = async () => {
    try {
      const cRes = await api.get('/messages/contacts');
      setContacts(cRes.data);
      const sRes = await api.get('/messages/my-schools');
      setMySchools(sRes.data);
      const mRes = await api.get('/messages/my-majors');
      setMyMajors(mRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchHistory = async () => {
    if (!selectedTarget) return;
    try {
      let endpoint = '';
      if (tab === 0) endpoint = `/messages/history/${selectedTarget.id}`;
      else if (tab === 1) endpoint = `/messages/school-history/${selectedTarget.id}`;
      else endpoint = `/messages/major-history/${selectedTarget.name}`;

      const response = await api.get(endpoint);
      setMessages(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchLists();
  }, []);

  useEffect(() => {
    fetchHistory();
    const interval = setInterval(fetchHistory, 5000);
    return () => clearInterval(interval);
  }, [selectedTarget, tab]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || !selectedTarget) return;

    try {
      let endpoint = '';
      let body: any = { content };

      if (tab === 0) {
        endpoint = '/messages/send';
        body.receiverId = selectedTarget.id;
      } else if (tab === 1) {
        endpoint = '/messages/school-send';
        body.schoolId = selectedTarget.id;
      } else {
        endpoint = '/messages/major-send';
        body.majorName = selectedTarget.name;
      }
      
      await api.post(endpoint, body);
      setContent('');
      fetchHistory();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box sx={{ height: { xs: 'auto', md: '75vh' }, mb: { xs: 2, md: 0 } }}>
      <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>การปรึกษาและข้อความ</Typography>
      
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Sidebar: Private / School / Major */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ height: { xs: 300, md: '100%' }, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: 2, borderRadius: 2 }}>
            <Tabs 
              value={tab} 
              onChange={(e, v) => { setTab(v); setSelectedTarget(null); setMessages([]); }} 
              variant="fullWidth"
            >
              <Tab icon={<PersonIcon />} label="ส่วนตัว" sx={{ minWidth: 0, fontSize: '0.75rem' }} />
              <Tab icon={<SchoolIcon />} label="โรงเรียน" sx={{ minWidth: 0, fontSize: '0.75rem' }} />
              <Tab icon={<MajorIcon />} label="สาขา" sx={{ minWidth: 0, fontSize: '0.75rem' }} />
            </Tabs>
            <Divider />
            <List sx={{ overflowY: 'auto', flexGrow: 1 }}>
              {tab === 0 && contacts.map((c: any) => (
                <ListItem 
                  key={c.id} 
                  component="div"
                  onClick={() => setSelectedTarget(c)} 
                  sx={{ cursor: 'pointer', bgcolor: selectedTarget?.id === c.id ? 'action.selected' : 'transparent' }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: c.role === 'SUPERVISOR' ? 'primary.main' : 'secondary.main', width: 32, height: 32 }}>
                      {c.first_name?.[0] || c.username[0].toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 'bold' }}>{c.first_name} {c.last_name}</Typography>} secondary={c.role} />
                </ListItem>
              ))}
              {tab === 1 && schools.map((s: any) => (
                <ListItem 
                  key={s.id} 
                  component="div"
                  onClick={() => setSelectedTarget(s)} 
                  sx={{ cursor: 'pointer', bgcolor: selectedTarget?.id === s.id ? 'action.selected' : 'transparent' }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}><SchoolIcon sx={{ fontSize: 18 }} /></Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 'bold' }}>{s.name}</Typography>} secondary="กลุ่มแชทโรงเรียน" />
                </ListItem>
              ))}
              {tab === 2 && majors.map((m: any) => (
                <ListItem 
                  key={m.name} 
                  component="div"
                  onClick={() => setSelectedTarget(m)} 
                  sx={{ cursor: 'pointer', bgcolor: selectedTarget?.name === m.name ? 'action.selected' : 'transparent' }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}><MajorIcon sx={{ fontSize: 18 }} /></Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 'bold' }}>{m.name}</Typography>} secondary="กลุ่มแชทสาขาวิชา" />
                </ListItem>
              ))}
              {((tab === 0 && contacts.length === 0) || (tab === 1 && schools.length === 0) || (tab === 2 && majors.length === 0)) && (
                <Typography sx={{ p: 2, textAlign: 'center' }} color="textSecondary" variant="body2">ไม่พบรายการ</Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Chat Area */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ height: { xs: 500, md: '100%' }, display: 'flex', flexDirection: 'column', bgcolor: '#f9f9f9', boxShadow: 2, borderRadius: 2, overflow: 'hidden' }}>
            {selectedTarget ? (
              <>
                <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #ddd' }}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    {tab === 0 ? `${selectedTarget.first_name} ${selectedTarget.last_name}` : 
                     tab === 1 ? selectedTarget.name : `กลุ่มวิชา${selectedTarget.name}`}
                  </Typography>
                </Box>
                
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {messages.map((m: any) => (
                    <Box 
                      key={m.id} 
                      sx={{ 
                        alignSelf: m.sender_user_id === user.id ? 'flex-end' : 'flex-start',
                        maxWidth: { xs: '85%', sm: '70%' }
                      }}
                    >
                      {tab !== 0 && m.sender_user_id !== user.id && (
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          {m.sender_name} ({m.sender_role})
                        </Typography>
                      )}
                      <Paper sx={{ 
                        p: 1.5, 
                        bgcolor: m.sender_user_id === user.id ? 'primary.main' : 'white',
                        color: m.sender_user_id === user.id ? 'white' : 'text.primary',
                        borderRadius: 2,
                        boxShadow: 1
                      }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{m.content}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, textAlign: 'right' }}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                  <div ref={chatEndRef} />
                </Box>

                <Box component="form" onSubmit={handleSendMessage} sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #ddd', display: 'flex', gap: 1 }}>
                  <TextField 
                    fullWidth 
                    size="small" 
                    placeholder="พิมพ์ข้อความ..." 
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                  <IconButton color="primary" type="submit" disabled={!content.trim()}>
                    <SendIcon />
                  </IconButton>
                </Box>
              </>
            ) : (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">กรุณาเลือกผู้ติดต่อหรือกลุ่มสนทนาเพื่อเริ่มใช้งาน</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GuidanceChat;
